/**
 * Lifecycle status of a single receipt-detail exception. Drives the
 * Outstanding Vendor Issues tab on POs and the Open Issues tab on
 * Receipts.
 *
 * State machine (transitions enforced server-side by the exception
 * service; each transition writes a `purchase_order_receipt_exception_event`
 * row so we can audit when each step happened and by whom):
 *
 *   OPEN ──> VENDOR_NOTIFIED ─┬── CREDIT_PENDING ──── CREDIT_RECEIVED ─┐
 *                              │                                        │
 *                              ├── REPLACEMENT_PENDING ── REPLACEMENT_RECEIVED ─┼──> RESOLVED
 *                              │                                        │
 *                              └── WRITTEN_OFF ─────────────────────────┘
 *
 *      └── direct close (receiver miskeyed, no vendor action) ──> RESOLVED
 *
 * Open states (require further action — show in Outstanding Vendor Issues):
 *   OPEN, VENDOR_NOTIFIED, CREDIT_PENDING, CREDIT_RECEIVED,
 *   REPLACEMENT_PENDING, REPLACEMENT_RECEIVED.
 *
 * CREDIT_RECEIVED and REPLACEMENT_RECEIVED are intermediate (NOT
 * terminal) — receipt of credit/replacement still requires explicit
 * RESOLVE by the manager so the AP / receiving team confirms the
 * matter is fully closed.
 *
 * Terminal states (no further action):
 *   RESOLVED, WRITTEN_OFF.
 *
 * Stored as varchar (not MySQL enum) for extensibility.
 */
export enum PurchaseOrderReceiptExceptionStatus {
	OPEN = 'OPEN',
	VENDOR_NOTIFIED = 'VENDOR_NOTIFIED',
	CREDIT_PENDING = 'CREDIT_PENDING',
	CREDIT_RECEIVED = 'CREDIT_RECEIVED',
	REPLACEMENT_PENDING = 'REPLACEMENT_PENDING',
	REPLACEMENT_RECEIVED = 'REPLACEMENT_RECEIVED',
	WRITTEN_OFF = 'WRITTEN_OFF',
	RESOLVED = 'RESOLVED',
}

export type PurchaseOrderReceiptExceptionStatusValue =
	`${PurchaseOrderReceiptExceptionStatus}`;

export const PurchaseOrderReceiptExceptionStatusLabels: Record<
	PurchaseOrderReceiptExceptionStatus,
	string
> = {
	[PurchaseOrderReceiptExceptionStatus.OPEN]: 'Open',
	[PurchaseOrderReceiptExceptionStatus.VENDOR_NOTIFIED]: 'Vendor Notified',
	[PurchaseOrderReceiptExceptionStatus.CREDIT_PENDING]: 'Credit Pending',
	[PurchaseOrderReceiptExceptionStatus.CREDIT_RECEIVED]: 'Credit Received',
	[PurchaseOrderReceiptExceptionStatus.REPLACEMENT_PENDING]:
		'Replacement Pending',
	[PurchaseOrderReceiptExceptionStatus.REPLACEMENT_RECEIVED]:
		'Replacement Received',
	[PurchaseOrderReceiptExceptionStatus.WRITTEN_OFF]: 'Written Off',
	[PurchaseOrderReceiptExceptionStatus.RESOLVED]: 'Resolved',
};

/**
 * Statuses that count as "open" for queue / rollup purposes —
 * everything that's not yet closed. The disposition derivation utils
 * use this set; UI filter chips on the PO "Outstanding Vendor Issues"
 * tab use it as the default selection.
 */
export const PURCHASE_ORDER_RECEIPT_EXCEPTION_OPEN_STATUSES: ReadonlyArray<PurchaseOrderReceiptExceptionStatus> =
	Object.freeze([
		PurchaseOrderReceiptExceptionStatus.OPEN,
		PurchaseOrderReceiptExceptionStatus.VENDOR_NOTIFIED,
		PurchaseOrderReceiptExceptionStatus.CREDIT_PENDING,
		PurchaseOrderReceiptExceptionStatus.CREDIT_RECEIVED,
		PurchaseOrderReceiptExceptionStatus.REPLACEMENT_PENDING,
		PurchaseOrderReceiptExceptionStatus.REPLACEMENT_RECEIVED,
	]);

/**
 * Statuses that count as "terminal" — exception is closed.
 */
export const PURCHASE_ORDER_RECEIPT_EXCEPTION_TERMINAL_STATUSES: ReadonlyArray<PurchaseOrderReceiptExceptionStatus> =
	Object.freeze([
		PurchaseOrderReceiptExceptionStatus.RESOLVED,
		PurchaseOrderReceiptExceptionStatus.WRITTEN_OFF,
	]);

export function isPurchaseOrderReceiptExceptionStatusOpen(
	status: PurchaseOrderReceiptExceptionStatus | string | null | undefined,
): boolean {
	if (status == null) {
		return false;
	}

	return PURCHASE_ORDER_RECEIPT_EXCEPTION_OPEN_STATUSES.includes(
		status as PurchaseOrderReceiptExceptionStatus,
	);
}

export function isPurchaseOrderReceiptExceptionStatusTerminal(
	status: PurchaseOrderReceiptExceptionStatus | string | null | undefined,
): boolean {
	if (status == null) {
		return false;
	}

	return PURCHASE_ORDER_RECEIPT_EXCEPTION_TERMINAL_STATUSES.includes(
		status as PurchaseOrderReceiptExceptionStatus,
	);
}
